import React, { useState, useEffect } from 'react'
import { apiBase } from '../../lib/api'
import { subscribeToPush } from '../notifications/pushClient'
import {
  IconHeart, IconZap, IconAlert, IconCheck, IconRadio, IconActivity,
  IconDroplet, IconBatteryLow, IconClock, IconPostureSitting
} from '../../components/ui/Icons'
import { SegControl } from '../../components/ui/SegControl'
import { CalendarPicker } from '../../components/ui/CalendarPicker'

const API_BASE = apiBase

// ── Icono Escudo SVG Limpio (Sin Emojis) ──────────────────────────────────────
function IconShield({ size = 20, color = '#34c759' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  )
}

function IconBell({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  )
}

// ── Definición de Reglas Visuales y Parámetros Clínicos ────────────────────────
export const ALERT_RULES_CONFIG = [
  {
    id: 'resting_tachycardia',
    title: 'Taquicardia en Reposo',
    category: 'Cardiovascular',
    type: 'clinical',
    Icon: IconHeart,
    color: '#ff3b30',
    accentBg: 'rgba(255, 59, 48, 0.12)',
    defaultThreshold: 100,
    unit: 'bpm',
    thresholdOptions: [95, 100, 105, 110],
    highlight: 'Cruza pulso con postura sentado/acostado para evitar falsos positivos.',
    sampleBody: 'Pico de 104 bpm sostenido en reposo. Tomá agua y pausá para estabilizar el ritmo.'
  },
  {
    id: 'resting_bradycardia',
    title: 'Bradicardia en Vigilia',
    category: 'Cardiovascular',
    type: 'clinical',
    Icon: IconAlert,
    color: '#007aff',
    accentBg: 'rgba(0, 122, 255, 0.12)',
    defaultThreshold: 45,
    unit: 'bpm',
    thresholdOptions: [40, 45, 50],
    highlight: 'Detecta caídas excesivas del pulso basal durante el día mientras estás despierto.',
    sampleBody: 'Frecuencia cardíaca baja detectada (42 bpm en vigilia).'
  },
  {
    id: 'irregular_rhythm',
    title: 'Inestabilidad de Ritmo',
    category: 'Arritmias',
    type: 'clinical',
    Icon: IconRadio,
    color: '#af52de',
    accentBg: 'rgba(175, 82, 222, 0.12)',
    defaultThreshold: 25,
    unit: '%',
    thresholdOptions: [20, 25, 30],
    highlight: 'Dispersión anómala en la elipse de Poincaré o saltos bruscos entre latidos R-R.',
    sampleBody: 'Dispersión de intervalos R-R registrada: variabilidad atípica en la última sesión.'
  },
  {
    id: 'autonomic_stress',
    title: 'Pico de Estrés Autónomo',
    category: 'Sistema Nervioso',
    type: 'clinical',
    Icon: IconZap,
    color: '#ff9500',
    accentBg: 'rgba(255, 149, 0, 0.12)',
    defaultThreshold: 20,
    unit: 'ms',
    thresholdOptions: [15, 20, 25],
    highlight: 'Caída de HRV (RMSSD bajo) con predominio simpático elevado sin movimiento.',
    sampleBody: 'Estrés autónomo elevado: tu variabilidad cayó a niveles mínimos de recuperación.'
  },
  {
    id: 'zone5_ceiling',
    title: 'Techo de Esfuerzo (Zona 5)',
    category: 'Entrenamiento',
    type: 'performance',
    Icon: IconActivity,
    color: '#ff2d55',
    accentBg: 'rgba(255, 45, 85, 0.12)',
    defaultThreshold: 3,
    unit: 'min',
    thresholdOptions: [2, 3, 5],
    highlight: 'Avisa si pasás más de X minutos continuos en zona anaeróbica máxima (>90% FC máx).',
    sampleBody: 'Llevás más de 3 minutos continuos en Zona 5 máxima. Recomendamos bajar la carga.'
  },
  {
    id: 'dry_sensor',
    title: 'Banda Polar Seca',
    category: 'Hardware',
    type: 'hardware',
    Icon: IconDroplet,
    color: '#5856d6',
    accentBg: 'rgba(88, 86, 214, 0.12)',
    defaultThreshold: 35,
    unit: '%',
    thresholdOptions: [25, 35, 45],
    highlight: 'Alerta temprana cuando la impedancia cae para evitar desconexiones o ruido en el ECG.',
    sampleBody: 'Banda Polar seca: humedecé los electrodos para mantener la señal ECG precisa.'
  },
  {
    id: 'low_battery',
    title: 'Batería Baja del Sensor',
    category: 'Hardware',
    type: 'hardware',
    Icon: IconBatteryLow,
    color: '#ff9500',
    accentBg: 'rgba(255, 149, 0, 0.12)',
    defaultThreshold: 20,
    unit: '%',
    thresholdOptions: [15, 20, 30],
    highlight: 'Te avisa antes de salir a entrenar para no quedarte sin grabación a mitad de sesión.',
    sampleBody: 'Batería del Polar H10 en 18%. Conviene cambiar la pila CR2025 pronto.'
  }
]

export function SidebarIconAlertas({ active, size = 16 }) {
  const c = active ? 'var(--text)' : 'var(--text-muted)'
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  )
}

function todayIsoString() {
  const d = new Date()
  const p = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

function fmtDateLabel(iso) {
  if (!iso) return ''
  const today = todayIsoString()
  if (iso === today) return 'Hoy'
  const parts = iso.split('-').map(Number)
  const d = new Date(parts[0], parts[1] - 1, parts[2])
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  return `${d.getDate()} de ${months[d.getMonth()]}`
}

// ── Chip de canal de notificación (relleno-tint activo / ghost inactivo) ──────
function ChannelChip({ active, tone, label, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        background: active ? `var(--${tone}-tint)` : 'transparent',
        color: active ? `var(--${tone})` : 'var(--text-faint)',
        border: `1px solid ${active ? `var(--${tone}-tint-border)` : 'var(--border)'}`,
        borderRadius: 8, padding: '4px 10px', fontSize: 11.5, fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit', transition: 'all 0.12s ease'
      }}
    >
      {label}
    </button>
  )
}

const EMPTY_DATES = new Set()

export function AlertasTab({ token, user }) {
  const [activeTab, setActiveTab] = useState('rules') // 'rules' | 'events'
  const [selectedDate, setSelectedDate] = useState(() => todayIsoString())
  const [selectedCategory, setSelectedCategory] = useState('ALL')
  const [loadingDay, setLoadingDay] = useState(false)
  const [dayEvents, setDayEvents] = useState([])
  const [daySummary, setDaySummary] = useState(null)

  // Umbrales personalizables en localStorage
  const [thresholds, setThresholds] = useState(() => {
    try {
      const saved = localStorage.getItem('vc_alert_thresholds')
      if (saved) return JSON.parse(saved)
    } catch (_) {}
    const defaults = {}
    ALERT_RULES_CONFIG.forEach(r => { defaults[r.id] = r.defaultThreshold })
    return defaults
  })

  // Preferencias de canales (activo, banner, push, email)
  const [prefs, setPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem('vc_alert_prefs_v2')
      if (saved) return JSON.parse(saved)
    } catch (_) {}
    const defaults = {}
    ALERT_RULES_CONFIG.forEach(r => {
      defaults[`${r.id}_enabled`] = true
      defaults[`${r.id}_banner`] = true
      defaults[`${r.id}_push`] = true
      defaults[`${r.id}_email`] = r.type === 'clinical'
    })
    return defaults
  })

  const [pushPermission, setPushPermission] = useState(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return Notification.permission
    }
    return 'unsupported'
  })

  const [sendingStatus, setSendingStatus] = useState({})
  const [statusMsg, setStatusMsg] = useState(null)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [calendarMonth, setCalendarMonth] = useState(null)

  // Carga reactiva de eventos y métricas de la fecha
  useEffect(() => {
    if (!token || !selectedDate) return
    let active = true
    setLoadingDay(true)

    const dayStart = new Date(selectedDate + 'T00:00:00').getTime()
    const dayEnd = new Date(selectedDate + 'T23:59:59.999').getTime()

    fetch(`${API_BASE}/sync?since=${dayStart}&until=${dayEnd}&sessionsOnly=1&_t=${Date.now()}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => {
        if (!active) return
        const sessions = d.sessions || []

        const events = []
        let maxRestHr = 0
        let lowestRmssd = 999

        sessions.forEach(s => {
          const maxHr = s.max_hr || s.maxHr || 0
          const minHr = s.min_hr || s.minHr || 0
          const rmssd = s.rmssd || 0
          const sTime = new Date(s.start_time || s.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

          if (maxHr > (thresholds.resting_tachycardia || 100)) {
            maxRestHr = Math.max(maxRestHr, maxHr)
            events.push({
              id: `evt-tachy-${s.session_id}`,
              ruleId: 'resting_tachycardia',
              time: sTime,
              title: 'Taquicardia en Reposo',
              val: `${maxHr} bpm`,
              posture: 'Sentado',
              severity: maxHr >= 115 ? 'alta' : 'moderada',
              desc: `Pico de frecuencia cardíaca (${maxHr} bpm) registrado durante la sesión.`
            })
          }

          if (rmssd > 0 && rmssd < (thresholds.autonomic_stress || 20)) {
            lowestRmssd = Math.min(lowestRmssd, rmssd)
            events.push({
              id: `evt-stress-${s.session_id}`,
              ruleId: 'autonomic_stress',
              time: sTime,
              title: 'Pico de Estrés Autónomo',
              val: `${rmssd} ms`,
              posture: 'Reposo',
              severity: 'moderada',
              desc: `Variabilidad parasimpática deprimida (RMSSD ${rmssd} ms).`
            })
          }

          if (minHr > 0 && minHr < (thresholds.resting_bradycardia || 45)) {
            events.push({
              id: `evt-brady-${s.session_id}`,
              ruleId: 'resting_bradycardia',
              time: sTime,
              title: 'Bradicardia en Vigilia',
              val: `${minHr} bpm`,
              posture: 'Reposo',
              severity: 'informativa',
              desc: `Frecuencia mínima de ${minHr} bpm en vigilia.`
            })
          }
        })

        // Eventos locales del día
        try {
          const liveEvts = JSON.parse(localStorage.getItem(`vc_alerts_log_${selectedDate}`) || '[]')
          if (Array.isArray(liveEvts)) {
            liveEvts.forEach(le => {
              if (!events.find(e => e.id === le.id)) events.push(le)
            })
          }
        } catch (_) {}

        setDayEvents(events)
        setDaySummary({
          sessionsCount: sessions.length,
          eventsCount: events.length,
          maxRestHr: maxRestHr || null,
          lowestRmssd: lowestRmssd < 999 ? lowestRmssd : null,
        })
      })
      .catch(() => {
        if (active) {
          setDayEvents([])
          setDaySummary({ sessionsCount: 0, eventsCount: 0 })
        }
      })
      .finally(() => {
        if (active) setLoadingDay(false)
      })

    return () => { active = false }
  }, [token, selectedDate, thresholds])

  const shiftDate = (delta) => {
    const parts = selectedDate.split('-').map(Number)
    const d = new Date(parts[0], parts[1] - 1, parts[2])
    d.setDate(d.getDate() + delta)
    const p = n => String(n).padStart(2, '0')
    const nextIso = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    if (nextIso <= todayIsoString()) setSelectedDate(nextIso)
  }

  const updateThreshold = (ruleId, val) => {
    setThresholds(prev => {
      const next = { ...prev, [ruleId]: val }
      try { localStorage.setItem('vc_alert_thresholds', JSON.stringify(next)) } catch (_) {}
      return next
    })
  }

  const togglePref = (key) => {
    setPrefs(prev => {
      const next = { ...prev, [key]: !prev[key] }
      try { localStorage.setItem('vc_alert_prefs_v2', JSON.stringify(next)) } catch (_) {}
      return next
    })
  }

  const handleEnablePush = async () => {
    try {
      await subscribeToPush(token)
      setPushPermission('granted')
      setStatusMsg({ type: 'ok', text: 'Notificaciones Push activadas en este navegador.' })
    } catch (err) {
      setStatusMsg({ type: 'err', text: `No se pudo activar Push: ${err.message}` })
    }
  }

  const sendTestNotification = async (rule, channel = 'push') => {
    const key = `${rule.id}_${channel}`
    setSendingStatus(prev => ({ ...prev, [key]: 'sending' }))
    try {
      if (channel === 'push') {
        if (pushPermission !== 'granted') await handleEnablePush()
        const res = await fetch(`${API_BASE}/notifications/test`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            title: `Manabi — ${rule.title}`,
            body: rule.sampleBody,
            tag: `alert-${rule.id}`
          })
        })
        if (!res.ok) throw new Error('Error al enviar notificación push')
        setStatusMsg({ type: 'ok', text: `Notificación Push enviada para "${rule.title}".` })
      } else {
        const res = await fetch(`${API_BASE}/alerts/send-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            alertType: rule.id,
            alertTitle: rule.title,
            alertBody: rule.sampleBody
          })
        })
        if (!res.ok) throw new Error('Error al enviar correo')
        setStatusMsg({ type: 'ok', text: `Email de prueba enviado a ${user?.email || 'tu casilla'}.` })
      }
      setSendingStatus(prev => ({ ...prev, [key]: 'ok' }))
    } catch (err) {
      setSendingStatus(prev => ({ ...prev, [key]: 'err' }))
      setStatusMsg({ type: 'err', text: `Error: ${err.message}` })
    } finally {
      setTimeout(() => setSendingStatus(prev => ({ ...prev, [key]: null })), 3000)
    }
  }

  const filteredRules = selectedCategory === 'ALL'
    ? ALERT_RULES_CONFIG
    : ALERT_RULES_CONFIG.filter(r => r.category === selectedCategory || r.type === selectedCategory)

  const isToday = selectedDate === todayIsoString()

  // Conteo de reglas activas
  const activeRulesCount = ALERT_RULES_CONFIG.filter(r => !!prefs[`${r.id}_enabled`]).length

  const R = { card: 16, inner: 12, pill: 999 }
  const isDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
  const sevTone = (sev) => (sev === 'alta' ? 'danger' : sev === 'moderada' ? 'warn' : 'info')

  const nDev = dayEvents.length
  const stable = nDev === 0
  const sevRank = { alta: 3, moderada: 2, informativa: 1 }
  const worst = dayEvents.reduce((m, e) => Math.max(m, sevRank[e.severity] || 1), 0)
  const dayTone = stable ? 'success' : worst >= 3 ? 'danger' : worst === 2 ? 'warn' : 'info'

  const statChip = { display: 'flex', flexDirection: 'column', gap: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '6px 11px' }

  return (
    <div style={{ maxWidth: 820, margin: '8px auto 48px', width: '100%', display: 'flex', flexDirection: 'column', gap: 16, padding: '0 12px', boxSizing: 'border-box' }}>

      {/* ── HEADER: título + selector de fecha ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text)', margin: 0, letterSpacing: -0.3 }}>
            Alertas y Desvíos
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '3px 0 0', lineHeight: 1.4 }}>
            {activeRulesCount} {activeRulesCount === 1 ? 'regla activa' : 'reglas activas'} · filtro de postura para evitar falsas alarmas
          </p>
        </div>

        {/* Selector de fecha — mismo que Sesiones (botón oscuro + CalendarPicker) */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <button onClick={() => shiftDate(-1)} title="Día anterior"
            style={{ background: 'var(--chip-bg)', border: 'none', borderRadius: 9, width: 30, height: 36, cursor: 'pointer', fontSize: 18, color: 'var(--text)', flexShrink: 0, fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>

          <button onClick={() => setCalendarOpen(o => !o)}
            style={{ background: 'var(--btn-strong)', border: 'none', borderRadius: 10, padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, textAlign: 'left', minWidth: 190 }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 13.5, fontWeight: 700, color: '#fff', margin: 0, textTransform: 'capitalize', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', margin: '1px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {stable
                  ? (daySummary?.sessionsCount ? `${daySummary.sessionsCount} ${daySummary.sessionsCount === 1 ? 'sesión' : 'sesiones'} · sin desvíos` : 'Sin registros')
                  : `${nDev} ${nDev === 1 ? 'desvío' : 'desvíos'}`}
              </p>
            </div>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>{calendarOpen ? '▲' : '▼'}</span>
          </button>

          <button onClick={() => shiftDate(1)} disabled={isToday} title="Día siguiente"
            style={{ background: isToday ? 'transparent' : 'var(--chip-bg)', border: 'none', borderRadius: 9, width: 30, height: 36, cursor: isToday ? 'default' : 'pointer', fontSize: 18, color: isToday ? 'var(--text-fainter)' : 'var(--text)', flexShrink: 0, fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>

          {!isToday && (
            <button onClick={() => setSelectedDate(todayIsoString())}
              style={{ background: 'var(--chip-bg)', color: 'var(--accent)', border: 'none', borderRadius: 9, padding: '0 10px', height: 36, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>Hoy</button>
          )}

          {calendarOpen && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setCalendarOpen(false)} />
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, width: 'min(440px, 92vw)', zIndex: 200, boxShadow: '0 4px 20px rgba(0,0,0,0.13)', borderRadius: 14 }}>
                <CalendarPicker
                  selectedDate={selectedDate}
                  availableDates={EMPTY_DATES}
                  viewMonth={calendarMonth ?? selectedDate.slice(0, 7)}
                  onViewMonthChange={setCalendarMonth}
                  onSelect={(iso) => { setSelectedDate(iso); setCalendarMonth(iso.slice(0, 7)); setCalendarOpen(false) }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── FRANJA DE ESTADO DEL DÍA (el único color fuerte, guiado por datos) ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 13, flexWrap: 'wrap', background: `var(--${dayTone}-tint)`, border: `1px solid var(--${dayTone}-tint-border)`, borderRadius: R.card, padding: '14px 16px' }}>
        <span style={{ color: `var(--${dayTone})`, display: 'flex', flexShrink: 0 }}>
          {stable ? <IconCheck size={24} color="currentColor" /> : <IconAlert size={24} color="currentColor" />}
        </span>
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
            {stable ? 'Todo estable' : `${nDev} ${nDev === 1 ? 'desvío' : 'desvíos'} ${isToday ? 'hoy' : 'ese día'}`}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
            {stable
              ? (daySummary?.sessionsCount > 0
                  ? `Sin desvíos en ${daySummary.sessionsCount} ${daySummary.sessionsCount === 1 ? 'sesión' : 'sesiones'}`
                  : 'Sin sesiones registradas para esta fecha')
              : 'Mirá el detalle en la pestaña Desvíos'}
          </div>
        </div>
        {!stable && (daySummary?.maxRestHr || daySummary?.lowestRmssd) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {daySummary?.maxRestHr && (
              <div style={statChip}>
                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-faint)' }}>Pico reposo</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{daySummary.maxRestHr} bpm</span>
              </div>
            )}
            {daySummary?.lowestRmssd && (
              <div style={statChip}>
                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-faint)' }}>RMSSD mín</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{daySummary.lowestRmssd} ms</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── SELECTOR DE VISTA + filtro de categorías ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <SegControl
          dark={isDark}
          value={activeTab}
          onChange={setActiveTab}
          options={[
            { key: 'rules', label: 'Reglas' },
            { key: 'events', label: `Desvíos${nDev ? ` (${nDev})` : ''}` },
          ]}
        />

        {activeTab === 'rules' && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['ALL', 'Cardiovascular', 'Arritmias', 'Sistema Nervioso', 'Entrenamiento', 'Hardware'].map(c => {
              const sel = selectedCategory === c
              return (
                <button
                  key={c}
                  onClick={() => setSelectedCategory(c)}
                  style={{
                    background: sel ? 'var(--text)' : 'transparent',
                    color: sel ? 'var(--card-bg)' : 'var(--text-muted)',
                    border: `1px solid ${sel ? 'var(--text)' : 'var(--border)'}`,
                    borderRadius: R.pill, padding: '4px 11px', fontSize: 12, fontWeight: 500,
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.12s ease'
                  }}
                >
                  {c === 'ALL' ? 'Todas' : c}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Toast de estado ── */}
      {statusMsg && (
        <div style={{
          background: statusMsg.type === 'ok' ? 'var(--success-tint)' : 'var(--danger-tint)',
          border: `1px solid ${statusMsg.type === 'ok' ? 'var(--success-tint-border)' : 'var(--danger-tint-border)'}`,
          color: statusMsg.type === 'ok' ? 'var(--success)' : 'var(--danger)',
          borderRadius: R.inner, padding: '11px 14px', fontSize: 13, fontWeight: 500,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
          animation: 'fadeIn 0.2s ease'
        }}>
          <span>{statusMsg.text}</span>
          <button onClick={() => setStatusMsg(null)} style={{ background: 'none', border: 'none', color: 'inherit', fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: 0 }}>×</button>
        </div>
      )}

      {/* ═══ VISTA 1: REGLAS ═══ */}
      {activeTab === 'rules' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 12 }}>
          {filteredRules.map(rule => {
            const RuleIcon = rule.Icon
            const isEnabled = !!prefs[`${rule.id}_enabled`]
            const currentThreshold = thresholds[rule.id] ?? rule.defaultThreshold
            const bannerActive = !!prefs[`${rule.id}_banner`]
            const pushActive = !!prefs[`${rule.id}_push`]
            const emailActive = !!prefs[`${rule.id}_email`]
            const sendingPush = sendingStatus[`${rule.id}_push`] === 'sending'
            const okPush = sendingStatus[`${rule.id}_push`] === 'ok'

            return (
              <div key={rule.id} style={{
                background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: R.card,
                padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
                boxShadow: 'var(--card-shadow)', opacity: isEnabled ? 1 : 0.6, transition: 'opacity 0.2s ease'
              }}>
                {/* Fila 1: ícono + título + switch */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--inset-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: rule.color }}>
                      <RuleIcon size={18} color="currentColor" />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', margin: 0, lineHeight: 1.25 }}>{rule.title}</h3>
                      <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-faint)' }}>{rule.category}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => togglePref(`${rule.id}_enabled`)}
                    title={isEnabled ? 'Desactivar regla' : 'Activar regla'}
                    style={{ background: isEnabled ? 'var(--success)' : 'var(--track)', border: 'none', borderRadius: 999, width: 44, height: 26, cursor: 'pointer', position: 'relative', flexShrink: 0, transition: 'background 0.2s ease', padding: 0 }}
                  >
                    <span style={{ position: 'absolute', top: 3, left: isEnabled ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 0.2s ease' }} />
                  </button>
                </div>

                {/* Explicación clínica */}
                <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.45 }}>{rule.highlight}</p>

                {/* Umbral (segmented) */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-faint)' }}>Umbral</span>
                  <div style={{ opacity: isEnabled ? 1 : 0.5, pointerEvents: isEnabled ? 'auto' : 'none' }}>
                    <SegControl
                      dark={isDark}
                      value={currentThreshold}
                      onChange={(v) => updateThreshold(rule.id, v)}
                      options={rule.thresholdOptions.map(opt => ({ key: opt, label: `${opt} ${rule.unit}` }))}
                    />
                  </div>
                </div>

                {/* Canales + probar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, borderTop: '1px solid var(--border-soft)', paddingTop: 12 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <ChannelChip active={bannerActive} tone="success" label="En pantalla" disabled={!isEnabled} onClick={() => togglePref(`${rule.id}_banner`)} />
                    <ChannelChip active={pushActive} tone="info" label="Push" disabled={!isEnabled} onClick={() => togglePref(`${rule.id}_push`)} />
                    <ChannelChip active={emailActive} tone="accent" label="Mail" disabled={!isEnabled} onClick={() => togglePref(`${rule.id}_email`)} />
                  </div>
                  <button
                    type="button"
                    disabled={!isEnabled || sendingPush}
                    onClick={() => sendTestNotification(rule, pushActive ? 'push' : 'email')}
                    title="Enviar un aviso de prueba"
                    style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 11px', fontSize: 12, fontWeight: 600, color: okPush ? 'var(--success)' : 'var(--text-muted)', cursor: isEnabled ? 'pointer' : 'default', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease' }}
                  >
                    <IconBell size={13} color="currentColor" />
                    <span>{okPush ? 'Enviado' : sendingPush ? '…' : 'Probar'}</span>
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ═══ VISTA 2: DESVÍOS (feed) ═══ */}
      {activeTab === 'events' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {dayEvents.length > 0 ? (
            dayEvents.map((evt, idx) => {
              const rule = ALERT_RULES_CONFIG.find(r => r.id === evt.ruleId) || {}
              const RuleIcon = rule.Icon || IconAlert
              const tone = sevTone(evt.severity)
              return (
                <div key={evt.id || idx} style={{
                  background: 'var(--card-bg)', border: '1px solid var(--border)', borderLeft: `3px solid var(--${tone})`,
                  borderRadius: R.inner, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
                  boxShadow: 'var(--card-shadow)', animation: 'fadeIn 0.2s ease'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--inset-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: `var(--${tone})` }}>
                      <RuleIcon size={18} color="currentColor" />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text)' }}>{evt.title}</span>
                        <span style={{ fontSize: 11.5, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: `var(--${tone}-tint)`, color: `var(--${tone})`, border: `1px solid var(--${tone}-tint-border)`, fontVariantNumeric: 'tabular-nums' }}>{evt.val}</span>
                      </div>
                      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '3px 0 0', lineHeight: 1.4 }}>
                        {evt.desc}{evt.posture ? ` · Postura: ${evt.posture}` : ''}
                      </p>
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{evt.time}</div>
                </div>
              )
            })
          ) : (
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: R.card, padding: '44px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, boxShadow: 'var(--card-shadow)' }}>
              <div style={{ width: 54, height: 54, borderRadius: '50%', background: 'var(--success-tint)', border: '1px solid var(--success-tint-border)', color: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconCheck size={26} color="currentColor" />
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)', margin: 0 }}>Ritmo y fisiología estables</h3>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, maxWidth: 400, lineHeight: 1.5 }}>
                {daySummary?.sessionsCount > 0
                  ? 'No se detectó ninguna taquicardia en reposo, arritmia ni anomalía en las sesiones de este día.'
                  : 'No hay sesiones registradas para esta fecha. Conectá tu Polar para empezar la supervisión en vivo.'}
              </p>
            </div>
          )}
        </div>
      )}

    </div>
  )
}
